import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useNotificationStore } from '../../../components/notificationStore';
import { useUserStore } from '../../../components/userStore';
import AuthorizedServiceModal from './AuthorizedServiceModal';

const ModifyCBO = () => {
	const [showServiceModal, setShowServiceModal] = useState(false);
	const [selectedExtServiceId, setSelectedExtServiceId] = useState(null);
	const { user } = useUserStore();
	const { agencyLinkId } = useParams();
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useNotificationStore();
	const agencyQuery = useQuery({
		queryKey: [
			'modify-credentialed-agency',
			user.coord_agency.id,
			agencyLinkId,
		],
		queryFn: async () => {
			const { data } = await axios.get(
				`/coord_agencies/${user.coord_agency.id}/credentialed-agencies/${agencyLinkId}`,
			);
			return data;
		},
		placeholderData: () => {
			return queryClient
				.getQueryData(['credentialed-agencies', user.coord_agency.id])
				?.find((d) => d.id === agencyLinkId);
		},
	});
	const servicesQuery = useQuery({
		queryKey: [
			'external-services',
			agencyQuery.data?.executing_coord_agency_id,
		],
		queryFn: async () => {
			const { data } = await axios.get(
				`/coord_agencies/${agencyQuery.data?.executing_coord_agency_id}/external-services-list`,
			);
			data.forEach((dataRow) => {
				dataRow.data = JSON.parse(dataRow.data);
			});
			return data;
		},
		enabled: Boolean(agencyQuery?.data?.executing_coord_agency_id),
	});
	const skuCountQuery = useQuery({
		queryKey: [
			'sku-counts',
			user.coord_agency.id,
			agencyQuery.data?.executing_coord_agency_id,
		],
		queryFn: async () => {
			const { data } = await axios.get(
				`/coord_agencies/${user.coord_agency.id}/exec-agency-skus/${agencyQuery.data?.executing_coord_agency_id}`,
			);
			return data;
		},
		enabled: Boolean(agencyQuery?.data?.executing_coord_agency),
	});
	const authServicesMutation = useMutation({
		mutationFn: async (authorizedServices) => {
			await axios.post(`/coord_agencies/authorized-services/${agencyLinkId}`, {
				authorizedServices,
			});
		},
		onSuccess: () => {
			showSuccess('Authorized Services updated');
			queryClient.invalidateQueries({
				queryKey: ['authorized-service'],
			});
		},
		onError: () => {
			showError('There was a problem updating Authorized Services');
		},
	});

	const handleOpenServiceModal = ({ target }) => {
		const { extServiceId } = target.dataset;
		setSelectedExtServiceId(extServiceId);
		setShowServiceModal(true);
	};

	const handleSave = (authorizedServices) => {
		setShowServiceModal(false);
		authServicesMutation.mutate(authorizedServices);
	};

	return (
		<>
			<div className="breadcrumbs">
				<ul>
					<li>
						<Link to="/" className="link">
							Home
						</Link>
					</li>
					<li>
						<Link to="/admin-panel" className="link">
							Admin Panel
						</Link>
					</li>
					<li>
						<Link to="/admin-panel/cred-cbos" className="link">
							Credentialed Billing Service Providers (CBOs)
						</Link>
					</li>
					<li>{agencyQuery?.data?.executing_coord_agency.agency_name}</li>
				</ul>
			</div>
			<div className="clear-both px-4 py-4 my-2 bg-white rounded-lg">
				<h2>{agencyQuery?.data?.executing_coord_agency.agency_name}</h2>
				<div className="divider" />
				<h3>Billing Authorized Services</h3>
				<div className="mt-4">
					{servicesQuery?.data?.map((service) => {
						const serviceData = service.data;
						const skuCount =
							skuCountQuery.isFetched &&
							skuCountQuery?.data?.find(
								({ external_211_services_id }) =>
									external_211_services_id === service.id,
							)?.skuCount;
						const hasSKUs = skuCount > 0;
						return (
							<div key={serviceData.id} className="p-2 odd:bg-gray-100">
								<div className="flex flex-row items-center gap-2 ">
									<div className="grow">
										<div>
											<strong>{serviceData.name}</strong>
										</div>
										<div className="text-xs">
											{serviceData.service_taxonomy
												.map(({ taxonomy_detail }) => taxonomy_detail)
												.sort()
												.join(', ')}
										</div>
									</div>
									<div className="uppercase">
										<strong>
											{!hasSKUs && (
												<span className="text-error">No SKUs available</span>
											)}
										</strong>
									</div>
									<div>
										<button
											disabled={!hasSKUs}
											className="btn btn-primary btn-sm"
											onClick={handleOpenServiceModal}
											data-ext-service-id={service.id}
										>
											Billing
										</button>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>
			<AuthorizedServiceModal
				isOpen={showServiceModal}
				onCancel={() => setShowServiceModal(false)}
				externalService={servicesQuery?.data?.find(
					({ id }) => id == selectedExtServiceId,
				)}
				agencyLinkId={agencyLinkId}
				onSave={handleSave}
			/>
		</>
	);
};

export default ModifyCBO;
